import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useToast } from "../../../Services/ToastContext";
import { PhoneNumberInput, GeneralButton } from "../../../components";
import {
  setUserId,
  setUserNumber,
} from "../../../redux/reducers/forgetPasswordSlice";
import { RootState } from "../../../redux/store";
import AuthLayout from "../../../Layout/AuthLayout";
import i18next, { TFunction } from "i18next";
import useForgetPasswordRequest, {
  ForgetPasswordRequest,
} from "../../../api/auth/forgetPassword/useForgetPasswordRequest";
import { useSendCodeViaSMS } from "../../../api/auth/forgetPassword/useSendCodeViaSMS";
import { Link } from "react-router-dom";
import arrow from "../../../assets/images/icons/chevron-right.svg";
import { useAppDispatch } from "../../../hooks";
import { isEnglish } from "../../../utils/utils";

interface ForgetPasswordFormInputs {
  phone: string;
}

const ForgetPassword: React.FC = () => {
  const { mutate: mutateCode } = useSendCodeViaSMS();
  const validate = useSelector((state: RootState) => state.phValidation);
  const phoneNumber = useSelector(
    (state: RootState) => state.registration.userDetails.PhoneNumber,
  );
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { setting } = useParams();
  const reduxDispatch = useAppDispatch(); 
  const navigate = useNavigate();

  // Create a function to generate the validation schema
  const createForgetPasswordSchema = (t: TFunction) => {
    return z.object({
      phone: z
        .string()
        .nonempty(t("phone_number_required"))
        .superRefine((value, ctx) => {
          if (!validate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("validation_configuration_error"),
            });
            return;
          }

          // Remove any non-numeric characters
          const phoneNumber = value.replace(/[^0-9]/g, "");
          const countryStart = validate.Start.toString();
          const countryLength = validate.Length;

          // Define the valid starting sequences
          const startWith0 = `0${countryStart}`;
          const startWithCountryStart = `${countryStart}`;

          if (phoneNumber.startsWith(startWith0)) {
            if (phoneNumber.length !== countryLength + 1) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("phone_number_must_be_and_start_with", {
                  length: countryLength + 1,
                  start: startWith0,
                }),
              });
            }
          } else if (phoneNumber.startsWith(startWithCountryStart)) {
            if (phoneNumber.length !== countryLength) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("phone_number_must_be_and_start_with", {
                  length: countryLength,
                  start: startWithCountryStart,
                }),
              });
            }
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("phone_number_must_start_with", {
                start1: startWithCountryStart,
                start2: startWith0,
              }),
            });
          }
        }),
    });
  };

  // Use useMemo to create the validation schema
  const validationSchema = useMemo(
    () => createForgetPasswordSchema(t),
    [t, validate],
  );

  // Initialize the form methods using react-hook-form and zodResolver
  const methods = useForm<ForgetPasswordFormInputs>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      phone: setting === "setting" ? phoneNumber : "",
    },
  });

  const {
    handleSubmit,
    setError,
    trigger,
    formState: { isSubmitted },
  } = methods;

  const { mutate: mutateForgetPassword, isPending } =
    useForgetPasswordRequest();

  // Re-trigger validation when language changes, but only if the form has been submitted
  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  // Handle form submission
  const onSubmit: SubmitHandler<ForgetPasswordFormInputs> = () => {
    // if (methods.formState.isValid) {
    handleConfirm();
    // }
  };

  // Confirm the forget password process
  const handleConfirm = useCallback(() => {
    const phone = methods.getValues("phone");

    // Remove leading '0' if present
    let phoneNumber = phone;
    if (phoneNumber.startsWith("0")) {
      phoneNumber = phoneNumber.substring(1);
    }

    // Do NOT prepend the country code
    const identification = phoneNumber;

    const requestData: ForgetPasswordRequest = {
      Identification: identification,
      CountryId: validate?.CountryId || 1,
    };
 

    mutateForgetPassword(requestData, {
      onSuccess: (data) => {
        if (data.Status && data.Data && data.Data.Id) {
          reduxDispatch(setUserId(data.Data.Id));
          reduxDispatch(setUserNumber(phoneNumber)); // Store the formatted phone number in Redux
           mutateCode(
            { Id: data.Data.Id },
            {
              onSuccess: () => {
                navigate(
                  setting === "setting"
                    ? "/verify-code-forget-password/setting"
                    : "/verify-code-forget-password",
                );
                showToast(t("verification_code_sent_successfully"), "success");
              },
              onError: (error: any) => { 
                showToast(error.message, "error");
              },
              onSettled: () => {
               },
            },
          );
        } else {
          // Display error under phone number input
          setError("phone", {
            type: "server",
            message: data.ErrorMessage || t("error_occurred"),
          });  
        }
      },
      onError: (error: any) => {
        // Display error under phone number input
        setError("phone", {
          type: "server",
          message:
            error.response?.data?.ErrorMessage ||
            error.message ||
            t("forget_password_failed"),
        }); 
      },
    });
  }, [
    methods,
    validate?.CountryId,
    mutateForgetPassword,
    reduxDispatch,
    mutateCode,
    navigate,
    showToast,
    setError,
    t,
  ]);

  // Helper function to format phone number for display
  const formatPhoneNumberForDisplay = (phone: string): string => {
    if (!validate) return phone;
    const countryStart = validate.Start.toString();
    const startWith0 = `0${countryStart}`;
    if (phone.startsWith(startWith0)) {
      return phone.slice(1); // Remove the leading '0'
    }
    return phone;
  };

  // Get the formatted phone number
  const formattedPhoneNumber = formatPhoneNumberForDisplay(
    methods.getValues("phone"),
  );

  // Full phone number with country code for display
  const displayPhoneNumber = `${validate?.Code || ""} ${formattedPhoneNumber}`;

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("forgot_password")}
        description={t("enter_phone_msg")}
        isWithLoginLink
      >
        <form
          className="flex w-full flex-col items-center gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-3 w-full lg:mb-20">
            <PhoneNumberInput readOnly={setting === "setting" ? true : false} />
          </div>
          <GeneralButton
            type="submit"
            title={t("send_code")}
            color="blue"
            block
            isLoading={isPending}
          /> 
        </form>
      </AuthLayout>
    </FormProvider>
  );
};

export default ForgetPassword;
